import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { CircularProgress, Dialog } from "@mui/material";
import { DialogHeader } from "@/components/DialogHeader";
import { toast } from "react-toastify";
import { TransactionType } from "@/types/transaction";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";
import {
  CheckoutSdk,
  OperationStatus,
  CheckoutSdkEvent,
} from "@/services/CheckoutSdk";
import * as Styled from "./DialogCashier.styles";

interface IDialogCashierProps {
  open: boolean;
  onClose: () => void;
  isLoading: boolean;
  iframeSrc: string;
  error: FetchBaseQueryError | SerializedError | undefined;
  transactionType: TransactionType;
}

export const DialogCashier: FC<IDialogCashierProps> = ({
  open,
  onClose,
  isLoading,
  iframeSrc,
  error,
  transactionType,
}) => {
  const [operationStatus, setOperationStatus] =
    useState<OperationStatus | null>(null);

  const finalUrl = useMemo(() => {
    if (iframeSrc) {
      const url = new URL(iframeSrc || "");
      url.searchParams.append("transactionType", transactionType);
      return url.toString();
    }
    return iframeSrc;
  }, [iframeSrc, transactionType]);

  const handleClose = useCallback(
    (operationStatus: OperationStatus | null) => {
      onClose();

      if (!operationStatus) {
        return;
      }

      const handlerByOperationStatus: Record<OperationStatus, () => void> = {
        [OperationStatus.Success]: () => {
          toast.success(`Thank you! Your order has been successfully placed!`, {
            autoClose: false,
          });
        },
        [OperationStatus.Error]: () => {
          toast.error(
            `Error. Your order has been failed. Please try again later.`,
            { autoClose: false },
          );
        },
        [OperationStatus.Pending]: () => {
          toast.warning(
            `Your operation is in progress. You can check the status later in the personal cabinet.`,
            { autoClose: false },
          );
        },
      };

      handlerByOperationStatus[operationStatus]?.();
    },
    [onClose],
  );

  useEffect(() => {
    return CheckoutSdk.subscribe(CheckoutSdkEvent.StatusChange, (data) => {
      setOperationStatus(data.operationStatus);
    });
  }, []);

  useEffect(() => {
    return CheckoutSdk.subscribe(CheckoutSdkEvent.GoBackToMerchant, (data) => {
      setOperationStatus(data.operationStatus);
      handleClose(data.operationStatus);
    });
  }, [handleClose]);

  return (
    <Dialog
      open={open}
      onClose={() => handleClose(operationStatus)}
      maxWidth={false}
    >
      <DialogHeader onClose={() => handleClose(operationStatus)} />
      <Styled.DialogContent dividers>
        {isLoading ? (
          <CircularProgress />
        ) : error ? (
          "Something went wrong. Please try again later."
        ) : (
          <Styled.IFrame src={finalUrl} allow="clipboard-write"></Styled.IFrame>
        )}
      </Styled.DialogContent>
    </Dialog>
  );
};
